import { useEffect, useState } from 'react';

import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconFarm, IconUsers } from '../../../common/icons';
import { useCombobox } from '../../../common/hooks/use-combobox';
import { useClientSearch } from '../contexts/search';

import '../styles/search.css';

// Reusable combined typeahead for clients + farms (GET /admin/clients/search).
// Pick a farm or a client; the consumer gets onSelect(item, type).
//   scope: 'all' (farms + clients) | 'clients'
// Used e.g. by the OPU form to pick the session's farm (and disambiguate
// look-alike farms by owner).

export default function ClientFarmSearch({
    onSelect,
    scope = 'all',
    placeholder = 'Buscar finca o cliente…',
    autoFocus = false,
    farmsOnly = false,
}) {
    const [text, setText] = useState('');
    const [debounced, setDebounced] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebounced(text), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [text]);

    const { data, isFetching } = useClientSearch(debounced, { scope });
    const farms = data?.farms ?? [];
    const clients = farmsOnly ? [] : (data?.clients ?? []);
    const term = debounced.trim();
    const hasResults = farms.length > 0 || clients.length > 0;

    const choose = (item, type) => {
        const label = type === 'farm'
            ? `${item.name}${item.owner?.name ? ` · ${item.owner.name}` : ''}`
            : item.name;
        setText(label);
        onSelect?.(item, type);
    };

    // Lista plana para el combobox: fincas primero, clientes después (mismo orden
    // visual del panel); los índices de opción siguen ese orden combinado.
    const options = [
        ...farms.map((f) => ({ item: f, type: 'farm' })),
        ...clients.map((c) => ({ item: c, type: 'client' })),
    ];
    const cb = useCombobox({ items: options, onChoose: (o) => choose(o.item, o.type) });

    return (
        <div className="search">
            <input
                type="text"
                className="search__input"
                value={text}
                placeholder={placeholder}
                autoFocus={autoFocus}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={cb.open}
                aria-controls={cb.listId}
                aria-activedescendant={cb.activeId}
                onChange={(e) => { setText(e.target.value); cb.setOpen(true); }}
                onFocus={cb.onFocus}
                onBlur={cb.onBlur}
                onKeyDown={cb.onKeyDown}
            />

            {cb.open && (
                <div className="search__panel" role="listbox" id={cb.listId}>
                    {term.length < 2 ? (
                        <p className="search__hint">Escribí al menos 2 caracteres.</p>
                    ) : !hasResults ? (
                        <p className="search__hint">{isFetching ? 'Buscando…' : 'Sin resultados.'}</p>
                    ) : (
                        <>
                            {farms.length > 0 && (
                                <div className="search__group">
                                    <span className="search__group-title">Fincas</span>
                                    {farms.map((f, i) => (
                                        <button
                                            key={`f-${f.id}`}
                                            type="button"
                                            id={cb.optionId(i)}
                                            role="option"
                                            aria-selected={cb.activeIndex === i}
                                            className={`search__item ${cb.activeIndex === i ? 'search__item--active' : ''}`}
                                            // select on mousedown + preventDefault so the input
                                            // never blurs first (which would close the panel).
                                            onMouseDown={cb.onOptionMouseDown({ item: f, type: 'farm' })}
                                            onMouseEnter={() => cb.setActive(i)}
                                        >
                                            <span className="search__item-icon"><IconFarm /></span>
                                            <span className="search__item-body">
                                                <span className="search__item-name">{f.name}</span>
                                                {f.owner?.name && <span className="search__item-sub">{f.owner.name}</span>}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {clients.length > 0 && (
                                <div className="search__group">
                                    <span className="search__group-title">Clientes</span>
                                    {clients.map((c, i) => (
                                        <button
                                            key={`c-${c.id}`}
                                            type="button"
                                            id={cb.optionId(farms.length + i)}
                                            role="option"
                                            aria-selected={cb.activeIndex === farms.length + i}
                                            className={`search__item ${cb.activeIndex === farms.length + i ? 'search__item--active' : ''}`}
                                            onMouseDown={cb.onOptionMouseDown({ item: c, type: 'client' })}
                                            onMouseEnter={() => cb.setActive(farms.length + i)}
                                        >
                                            <span className="search__item-icon"><IconUsers /></span>
                                            <span className="search__item-body">
                                                <span className="search__item-name">{c.name}</span>
                                                <span className="search__item-sub">
                                                    {[c.document, c.farms?.length ? `${c.farms.length} finca(s)` : null]
                                                        .filter(Boolean).join(' · ')}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
